import moment, { Moment } from 'moment';
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { date, object, SchemaOf } from 'yup';
import {
    Button,
    Icon,
    Loader,
    Modal, notification, NumberInput, DatePicker,
} from 'ui-library';
import { useSinglePortfolio } from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/context';
import { BUTTON_TYPE_PRIMARY } from 'ui-library/components/Button/constants';
import { ICON_TYPE_DELETE, ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import ServiceManager from 'services/ServiceManager';
import { useYupValidationResolver } from '../../../../../../../../hooks/useYupValidationResolver';

import './CustodyFeeModal.css';

import {
    addDaysToDate,
    compareDates, dateFormatZurich, formatDateWithoutLocalConversion, isQuarterEndDate,
} from '../../../../../../../../utils/datetime';
import { FEE_TYPES } from 'datasource/CreatePortfolio/constants';
import { FeeData } from 'pages/ClientOverview/pages/Portfolios/pages/SinglePortfolioView/context/context';

// --- Interfaces ---
interface CustodyFeeModalProps {
    handleClose: () => void;
    showModal: boolean;
    feeData?: FeeData;
    onSave: (feeType: number, data: any) => Promise<void> | void;
    defaultCustodyFee: number;
    onDeleteFee?: (data: FeeData) => void;
    showDeleteButton?: boolean;
}

interface FormValues {
    startDate: Moment | null;
    expiryDate: Moment | null;
}

// --- Component ---

const CustodyFeeModal: React.FC<CustodyFeeModalProps> = ({
    handleClose,
    showModal,
    feeData,
    onSave,
    defaultCustodyFee,
    onDeleteFee,
    showDeleteButton = false,
}) => {
    const { t } = useTranslation();

    // Explicitly typing state
    const [custodyFee, setCustodyFee] = useState<string | number>(feeData?.fee ?? defaultCustodyFee);
    const [discountValue, setDiscountValue] = useState<number>(feeData?.discountValue || 0);
    const [inProgress, setInProgress] = useState<boolean>(false);

    const schema: SchemaOf<FormValues> = useMemo(() => object().shape({
        startDate: date().required(t('validation.mandatoryField')).nullable(),
        expiryDate: date().nullable(),
    }) as any, [t]);

    const { investmentAppId } = useSinglePortfolio();

    useEffect(() => {
        if (!discountValue) {
            setDiscountValue(feeData?.discountValue || 0);
        }
    }, [feeData?.discountValue]);

    const resolver = useYupValidationResolver(schema);

    const defaultValues: FormValues = useMemo(() => ({
        startDate: feeData?.startDate ? moment.utc(feeData.startDate) : moment.utc(),
        expiryDate: feeData?.expiryDate ? moment.utc(feeData.expiryDate) : null,
    }), [feeData?.startDate, feeData?.expiryDate]);

    const {
        handleSubmit, formState: { errors, isDirty }, control, watch, reset,
    } = useForm<FormValues>({
        resolver,
        defaultValues,
    });

    // Re-sync form when feeData changes
    useEffect(() => {
        reset(defaultValues);
        setCustodyFee(feeData?.fee ?? defaultCustodyFee);
    }, [feeData, reset, defaultValues, defaultCustodyFee]);

    const formData = watch();

    const onClose = () => {
        reset(defaultValues);
        setCustodyFee(feeData?.fee ?? defaultCustodyFee);
        setDiscountValue(feeData?.discountValue || 0);
        handleClose();
    };

    const saveCustodyChanges = async () => {
        try {
            setInProgress(true);
            const finalData = {
                fee: typeof custodyFee === 'string' ? parseFloat(custodyFee) : custodyFee,
                discountValue: typeof discountValue === 'string' ? parseFloat(discountValue) : discountValue,
                startDate: formatDateWithoutLocalConversion(formData?.startDate, moment.defaultFormatUtc),
                expiryDate: formData?.expiryDate ? formatDateWithoutLocalConversion(formData?.expiryDate, moment.defaultFormatUtc) : null,
                feeType: FEE_TYPES.custody,
            };

            await onSave(FEE_TYPES.custody, finalData);
            handleClose();
        } finally {
            setInProgress(false);
        }
    };

    const onDelete = useCallback(async () => {
        try {
            setInProgress(true);
            await ServiceManager.customInvestmentService('deleteFee', [{
                investmentApplicationId: investmentAppId,
                feeType: FEE_TYPES.custody,
            }]);

            if (onDeleteFee) {
                onDeleteFee({
                    fee: defaultCustodyFee,
                    feeType: FEE_TYPES.custody,
                    startDate: moment(),
                    discountValue: 0,
                });
            }

            onClose();
            notification.open({ content: t('feeModal.feeDeletedSuccessfully'), type: 'success' });
        } catch (err) {
            notification.open({ content: t('general.somethingWentWrong'), type: 'error' });
        } finally {
            setInProgress(false);
        }
    }, [onDeleteFee, investmentAppId, defaultCustodyFee, t]);

    const confirmDeletion = useCallback(() => {
        Modal.confirm({
            title: t('feeModal.deleteDiscountConfirmation.title'),
            content: t('feeModal.deleteDiscountConfirmation.content'),
            onOk: onDelete,
        });
    }, [onDelete, t]);

    const renderFooter = useMemo(() => (
        <div className="CustodyFee__ButtonWrapper">
            <Button
                disabled={inProgress}
                onClick={confirmDeletion}
                type="danger"
                className={(!feeData?.discountValue || !showDeleteButton) ? 'hidden' : ''}
            >
                <Icon type={ICON_TYPE_DELETE} />
                {t('custodyFeeModal.buttons.deleteDiscount')}
            </Button>
            <Button disabled={inProgress} onClick={onClose}>
                {t('custodyFeeModal.buttons.cancel')}
            </Button>
            <Button
                type={BUTTON_TYPE_PRIMARY}
                disabled={(!isDirty && !discountValue) || inProgress}
                onClick={handleSubmit(() => saveCustodyChanges())}
            >
                {inProgress ? <Loader /> : t('custodyFeeModal.buttons.save')}
            </Button>
        </div>
    ), [inProgress, confirmDeletion, feeData?.discountValue, showDeleteButton, onClose, isDirty, discountValue, handleSubmit, t]);

    const handleDiscountChange = (val: any) => {
        const formattedValue = parseFloat(val) || 0;
        const tempCustodyFee = Math.max(defaultCustodyFee * (1 - formattedValue / 100), 0);

        setCustodyFee(tempCustodyFee?.toFixed(3).replace(/\.?0*$/, ''));
        setDiscountValue(formattedValue);
    };

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    return (
        <Modal
            title={t('custodyFeeModal.labels.title')}
            width={740}
            visible={showModal}
            onCancel={onClose}
            footer={renderFooter}
            keyboard={false}
            maskClosable={false}
            closable
            borderRadius
            closeIcon={closeIcon}
        >
            <form onSubmit={(e) => e.preventDefault()}>
                <div className="CustodyFee__FieldWrapper">
                    <p>{t('custodyFeeModal.labels.regularFee')}</p>
                    <p>
                        {defaultCustodyFee}
                        %
                    </p>
                </div>
                <div className="CustodyFee__FieldWrapper">
                    <p>{t('custodyFeeModal.labels.percentageFee')}</p>
                    <NumberInput
                        onChange={handleDiscountChange}
                        value={discountValue}
                        validation={{
                            maxValue: 100,
                            minValue: 0,
                        }}
                        allowNegativeValue={false}
                    />
                </div>
                <div className="CustodyFee__FieldWrapper">
                    <p>{t('custodyFeeModal.labels.startDate')}</p>
                    <Controller
                        control={control}
                        name="startDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={formData?.startDate && formatDateWithoutLocalConversion(formData.startDate, dateFormatZurich)}
                                format={dateFormatZurich}
                                data-cy="startDate"
                                error={errors?.startDate}
                                disabledDate={(current: Moment) => current && (
                                    (formData.expiryDate ? compareDates(current, formData.expiryDate) : false)
                                        || moment(current).isBefore(addDaysToDate(new Date(), -1))
                                )}
                                required
                            />
                        )}
                    />
                </div>
                <div className="CustodyFee__FieldWrapper">
                    <div className="CustodyFee__LabelWrapper">
                        <p>{t('custodyFeeModal.labels.expiryDate')}</p>
                        <span>{t('custodyFeeModal.labels.quarterDatesInfo')}</span>
                    </div>
                    <Controller
                        control={control}
                        name="expiryDate"
                        render={({ field }) => (
                            <DatePicker
                                {...field}
                                value={formData?.expiryDate && formatDateWithoutLocalConversion(formData.expiryDate, dateFormatZurich)}
                                format={dateFormatZurich}
                                data-cy="expiryDate"
                                errors={errors?.expiryDate}
                                disabledDate={(current: Moment) => current && (
                                    (formData.startDate ? compareDates(formData.startDate, current) : false)
                                        || isQuarterEndDate(current)
                                )}
                            />
                        )}
                    />
                </div>
                <div className="CustodyFee__FieldWrapper">
                    <p>{t('custodyFeeModal.labels.newFee')}</p>
                    <p>
                        <b>
                            {custodyFee}
                            %
                        </b>
                    </p>
                </div>
            </form>
        </Modal>
    );
};

export default CustodyFeeModal;
